import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Radio, X, Search, ExternalLink, ShieldCheck, FileText } from 'lucide-react';

interface MarqueeItem {
  id: string;
  type: string;
  category: string;
  text: string;
  url: string;
}

const MarqueeTicker: React.FC = () => {
  const [items, setItems] = useState<MarqueeItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MarqueeItem | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMarquee = async () => {
      try {
        const response = await api.get('/news/marquee');
        if (response.data && Array.isArray(response.data)) {
          setItems(response.data);
        }
      } catch (err) {
        console.error('Error fetching marquee ticker items:', err);
      }
    };
    fetchMarquee();
    const interval = setInterval(fetchMarquee, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const handleItemClick = (item: MarqueeItem, e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedItem(item);
  };

  return (
    <>
      <div style={{ 
        backgroundColor: '#0f172a', 
        borderBottom: '3px solid #ecc94b', 
        color: 'white', 
        padding: '0.75rem 1.75rem', 
        fontSize: '1.05rem', 
        fontWeight: '700', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1.25rem', 
        overflow: 'hidden', 
        whiteSpace: 'nowrap' 
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          backgroundColor: '#dc2626', 
          color: 'white', 
          padding: '0.4rem 0.9rem', 
          borderRadius: '6px', 
          fontSize: '0.85rem', 
          fontWeight: '800',
          textTransform: 'uppercase', 
          letterSpacing: '0.75px', 
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.4)'
        }}>
          <Radio size={16} /> LIVE MARQUEE
        </div>
        
        <div style={{ overflow: 'hidden', width: '100%', flex: 1 }}>
          {React.createElement('marquee', { 
            scrollamount: '4', 
            scrolldelay: '18',
            style: { 
              color: '#38bdf8', 
              fontSize: '1.1rem', 
              fontWeight: 'bold', 
              display: 'block' 
            } 
          }, 
            (!items || items.length === 0) ? (
              <span style={{ color: '#38bdf8' }}>
                LIVE UPDATES: Welcome to Elum Law Platform — Pakistan's AI-Powered Judicial Database & Legal Repository | Real-time Case Law & Statutory Notifications
              </span>
            ) : (
              items.map((item, idx) => {
                return (
                  <span key={item.id || idx} style={{ display: 'inline-block', marginRight: '4rem' }}>
                    <a 
                      href={item.url || '#'}
                      onClick={(e) => handleItemClick(item, e)}
                      style={{ 
                        color: '#38bdf8', 
                        textDecoration: 'none', 
                        fontWeight: '700',
                        transition: 'color 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.color = '#ecc94b')}
                      onMouseOut={(e) => (e.currentTarget.style.color = '#38bdf8')}
                    >
                      {item.text}
                    </a>
                  </span>
                );
              })
            )
          )}
        </div>
      </div>

      {/* INTERACTIVE MARQUEE TICKER DETAIL MODAL */}
      {selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.82)',
          backdropFilter: 'blur(8px)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1.5rem'
        }}>
          <div style={{
            backgroundColor: '#0f172a',
            color: 'white',
            border: '2px solid #ecc94b',
            borderRadius: '16px',
            maxWidth: '650px',
            width: '100%',
            boxShadow: '0 25px 60px rgba(0,0,0,0.8)',
            overflow: 'hidden',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 1.5rem',
              backgroundColor: '#1e293b',
              borderBottom: '1px solid rgba(236,201,75,0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '0.3rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '900',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}>
                  <Radio size={14} /> LIVE MARQUEE DISPATCH
                </div>
                <span style={{
                  backgroundColor: 'rgba(236,201,75,0.15)',
                  color: '#ecc94b',
                  padding: '0.25rem 0.65rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}>
                  {selectedItem.category || selectedItem.type || 'Legal Alert'}
                </span>
              </div>
              <button 
                onClick={() => setSelectedItem(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={22} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1.75rem', maxHeight: '65vh', overflowY: 'auto' }}>
              <h3 style={{
                margin: '0 0 1.25rem 0',
                color: '#ecc94b',
                fontSize: '1.25rem',
                lineHeight: '1.4',
                fontWeight: '800'
              }}>
                {selectedItem.text}
              </h3>

              <div style={{
                backgroundColor: 'rgba(30, 41, 59, 0.7)',
                border: '1px solid rgba(236, 201, 75, 0.2)',
                borderRadius: '10px',
                padding: '1.25rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#38bdf8', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  <ShieldCheck size={18} /> Judicial Repository Notice
                </div>
                <div style={{ fontSize: '0.82rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                  This alert is synchronized with Elum Law Platform's live judicial crawler and superior court publications. All case precedents and statutory acts are verified under High Court Practice Rules.
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    const cleanQuery = selectedItem.text.replace(/^[📜📰⚖️]\s*\[[^\]]+\]\s*/, '').replace(/—.*$/, '').trim();
                    setSelectedItem(null);
                    navigate(`/law-directory?query=${encodeURIComponent(cleanQuery || selectedItem.text)}`);
                  }}
                  style={{
                    flex: 1,
                    minWidth: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    backgroundColor: '#128c7e',
                    color: 'white',
                    padding: '0.75rem 1.25rem',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <Search size={18} /> Search in Law Directory
                </button>

                {selectedItem.url && selectedItem.url !== '#' && (
                  <button
                    onClick={() => {
                      if (selectedItem.url.startsWith('http')) {
                        window.open(selectedItem.url, '_blank');
                      } else {
                        setSelectedItem(null);
                        navigate(selectedItem.url);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: 'rgba(236,201,75,0.15)',
                      color: '#ecc94b',
                      border: '1px solid #ecc94b',
                      padding: '0.75rem 1.25rem',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    <ExternalLink size={18} /> Open Direct Link
                  </button>
                )}

                <button
                  onClick={() => setSelectedItem(null)}
                  style={{
                    padding: '0.75rem 1.25rem',
                    backgroundColor: '#334155',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default MarqueeTicker;
